import React, { useCallback, useEffect, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { CaseCourtBindingProvenance } from '@lds-efsp/shared-contracts';
import { FormRadioGroup } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import CaseSearch from './CaseSearch';
import { hydrateCaseRoster } from '../lib/hydrate-case-roster';
import { remapMetadataReferences } from '../lib/remap-metadata-references';
import { stampCaseCourtBinding } from '../lib/stamp-case-court-binding';

interface CaseAttachPanelProps {
  brand: BrandConfig;
  /** id for the CaseSearch container so Next-blocker jump links can scroll to it. */
  anchorId?: string;
  /**
   * Resolve the case category display NAME from the context's category
   * prefetch at attach time (WOL-443 / Walter R86). The document-types fetch
   * keys off `caseCategoryName`, and the classic backfill (a CaseInfoTab
   * effect) only runs while that tab is mounted — an Upload-step attach in the
   * confirm flow would otherwise leave the doc-type list locked until the
   * filer reaches the Confirm page. Opt-in so the classic Where-to-File mount
   * keeps its exact current behavior (CaseInfoTab backfills on step 3).
   */
  resolveCategoryName?: boolean;
}

/**
 * The existing-case attach: CaseSearch plus the subcase selector, with the
 * attach side-effects (case fields, roster hydration + `case-roster` tagging,
 * per-doc metadata reference remap). Extracted verbatim from OrderInfoTab
 * (WOL-443) so the confirm flow can mount the same lookup on the Upload step —
 * the looked-up case drives category/type and the roster wherever it happens.
 */
const CaseAttachPanel: React.FC<CaseAttachPanelProps> = ({ brand, anchorId, resolveCategoryName }) => {
  const {
    wizardState,
    confirmFlowEnabled,
    updateCaseInfo,
    setParties,
    setAttorneys,
    updateDocumentMetadata,
    revertCaseAttachSections,
    prefetchedCategories,
  } = useEFiling();
  // R95 (Walter, WOL-443): the subcase list is PERSISTED wizard state, not
  // component-local — the filer can attach on Upload and meet the
  // `subcase-required` gate on Confirm (a different mount), so every mount
  // must be able to render the picker. Legacy drafts lack the field.
  const persistedSubcases = wizardState.caseInfo.subcases;
  const subcases = useMemo(() => persistedSubcases ?? [], [persistedSubcases]);

  // The doc-type list keys off the case category's display NAME, but the
  // attach writes only the CODE (GetCase carries no display name) — the
  // classic backfill is a CaseInfoTab effect that only runs while that tab is
  // mounted. Resolve the name here from the county's prefetched reference
  // options, effect-shaped so it completes whichever lands last: the attach
  // or the prefetch. See the resolveCategoryName prop doc.
  const { caseCategoryCode, caseCategoryName } = wizardState.caseInfo;
  const county = wizardState.orderInfo.county;
  // R105 (Walter): seed the CaseSearch case-number input with the extraction-
  // stashed number (`trialCourtCaseNumber`, written by the R88 subsequent-filing
  // adoption) on the confirm-flow surface, and only while no case is attached —
  // `caseTrackingId` is set solely by a successful attach (handleCaseSelected),
  // so `!caseTrackingId` is the precise "no case attached" predicate. The
  // classic surface (confirmFlowEnabled === false) and an already-attached case
  // both yield `undefined`, so CaseSearch starts empty there. Both CaseAttachPanel
  // mounts (Upload `case-search-upload` via DocumentsTab + Confirm
  // `case-search-section` via OrderInfoTab) render this panel, so this single
  // seed covers each; empty-string stash normalizes to undefined so a no-
  // extraction / manual-filing-type session produces no seed either.
  const caseSeed =
    confirmFlowEnabled && !wizardState.caseInfo.caseTrackingId
      ? wizardState.caseInfo.trialCourtCaseNumber || undefined
      : undefined;
  useEffect(() => {
    if (!resolveCategoryName || !caseCategoryCode || caseCategoryName) return;
    const match = (prefetchedCategories[county] ?? [])
      .find(opt => opt.code === caseCategoryCode);
    if (match) updateCaseInfo({ caseCategoryName: match.name });
  }, [resolveCategoryName, caseCategoryCode, caseCategoryName, prefetchedCategories, county, updateCaseInfo]);

  // When a case is selected for subsequent filing, pre-populate wizard state
  const handleCaseSelected = useCallback((caseDetail: {
    trackingId: string;
    docketId: string;
    title: string;
    /** JTI CaseCategoryText — taxonomy inversion: actually CASE_TYPE code */
    category: string;
    /** JTI CaseTypeText — taxonomy inversion: actually CASE_CATEGORY code */
    caseTypeText: string;
    /** The court the CASE itself belongs to (SOR-182) — the CaseCourtBinding. */
    courtCode?: string;
    /** How the server attributed `courtCode` (SOR-192). See CaseSearch.CaseDetail. */
    courtCodeProvenance?: CaseCourtBindingProvenance;
    parties: Array<{
      id: string;
      referenceId?: string;
      firstName?: string;
      lastName?: string;
      organizationName?: string;
      role: string;
      isOrganization: boolean;
    }>;
    subcases?: Array<{
      id: string;
      type: string;
      title: string;
    }>;
    attorneys?: Array<{
      id: string;
      barNumber?: string;
      firstName?: string;
      lastName?: string;
      firmName?: string;
      partyId?: string;
    }>;
  }) => {
    // JTI taxonomy inversion:
    //   CaseCategoryText (caseDetail.category) = JTI CASE_CATEGORY = our caseType code
    //   CaseTypeText (caseDetail.caseTypeText) = JTI CASE_TYPE = our caseCategoryCode
    const subs = caseDetail.subcases ?? [];

    const autoSelect = subs.length <= 1;
    updateCaseInfo({
      subcases: subs,
      caseTitle: caseDetail.title,
      caseCategoryCode: caseDetail.caseTypeText,
      // SOR-196 (EARS-14): the attach writes the CASE's category CODE, so the
      // display NAME has to be RE-DERIVED from it — never left holding whatever
      // the uploaded document said. The backfill effect above resolves the name
      // from the court's own category list the moment this lands, and it only
      // runs while `caseCategoryName` is empty, so clearing it here is the whole
      // mechanism: without it a document-derived name survived the attach and
      // every reader of the field rendered it. That is how MCV089001 — whose
      // GetCase `CaseTypeText` is 411110, i.e. Civil Unlimited — showed as the
      // UD complaint's "Civil Limited" on the runs where extraction had
      // populated the field, and as Civil Unlimited only on the run where a
      // county toggle happened to clear it first. Same case, same court, two
      // different answers.
      //
      // Written UNCONDITIONALLY for the same reason the binding stamp below is:
      // a clear conditional on "a document name is present" would leave the
      // PREVIOUS case's resolved name in place across a re-attach, which is the
      // identical defect one case later. `''` is the canonical empty
      // `defaultWizardState` and `resetCourtScopedCaseData` both use, so no
      // reader needs a third representation of "not resolved yet".
      caseCategoryName: '',
      // The SOR-173 recovery pair goes with it. Both are readings of a
      // DOCUMENT's category — the banner quote of a name this court rejected,
      // and the candidate codes offered to repair it — and the case record has
      // now settled that question outright, so neither can still be describing
      // an open one. Mirrors `resetCourtScopedCaseData`, which clears the pair
      // wherever the category's court-scoped basis changes.
      ocrExtractedCategoryName: undefined,
      caseCategoryCandidates: undefined,
      caseType: { code: caseDetail.category, name: '' },
      trialCourtCaseNumber: caseDetail.docketId,
      caseTrackingId: caseDetail.trackingId,
      complaintId: autoSelect ? (subs[0]?.id || '') : '',
      complaintTitle: autoSelect ? (subs[0]?.title || '') : '',
      subcaseCount: subs.length,
      // SOR-182/191/192/193: the CaseCourtBinding pair — the court the lookup
      // asserted and the SOURCE that asserted it — persisted verbatim from the
      // response, synthesizing neither and defaulting neither. The rule and the
      // reasoning behind every branch of it live in the helper; note only that
      // it is written UNCONDITIONALLY, for the same reason `caseCategoryName`
      // above is: a conditional write would strand the PREVIOUS case's binding
      // across a re-attach.
      //
      // SPEC-SOR-198 R17: this spread is the wizard's ONLY non-empty writer of
      // the pair, and the helper takes the lookup response alone — the `county`
      // this panel holds is the REQUEST parameter and is deliberately not in
      // scope for it.
      ...stampCaseCourtBinding(caseDetail),
    });

    // Pre-populate parties + attorneys from the existing case. JTI requires either
    // FILING_ATTORNEY (when the party has a court-registered attorney) or
    // FILING_PARTY_ADDRESS (self-rep) on every subsequent doc. hydrateCaseRoster
    // collapses GetCase's per-subcase duplicate party records to one canonical
    // wizard-local party (SOR-219), links each attorney of record to that
    // canonical party (R3), and marks a filing-party-role party self-rep exactly
    // when no wizard-local attorney is linked to it (R4/R5) — so a represented
    // party is never flagged self-rep and asked for an address.
    //
    // Capture the pre-swap rosters so per-doc references can be remapped to the
    // new local ids below.
    const oldParties = wizardState.parties;
    const oldAttorneys = wizardState.attorneys;

    // SPEC-SOR-174 R18: the roster the draft ends up holding must be the NEWLY
    // ATTACHED case's own roster — UNCONDITIONALLY, including when that case
    // carries no parties and no attorneys. This replaces a pair of
    // `if (hasParties)` / `if (hasAttorneys)` guards that skipped the swap for
    // an empty incoming roster and so left the PREVIOUS case's entries in
    // place, still tagged `case-roster` and therefore still passing the R8
    // court-roster check — the replaced case's parties rode into the filing.
    // `roster_is_court_roster` alone cannot catch that (stale entries are
    // case-roster sourced too), which is why R18 states the obligation as
    // identity: whose roster the draft holds, not merely how it is tagged.
    //
    // Scope note: this half is NOT confirm-flow gated. It corrects the
    // wholesale-swap contract (SOR-226 R8) that already governs both flows —
    // the empty-incoming-roster case was simply the hole in it. Only the
    // guided-section revert below is new behaviour, and only that is gated.
    const hydrated = hydrateCaseRoster(caseDetail.parties, caseDetail.attorneys ?? []);
    // Source-attribute the minted roster `case-roster` at the call site (SOR-226,
    // R1) — NOT inside hydrateCaseRoster, which stays algorithm-only and
    // non-goal-protected (its property suites assert specific fields, never the
    // tag). `as const` is load-bearing so TS narrows the literal to EntrySource
    // rather than widening to string. The swap stays wholesale: setParties/
    // setAttorneys REPLACE the arrays, dropping any pre-existing
    // extraction/manual entries (R8) — the never-drop merge is scoped to
    // applyExtraction alone (R7), not to this swap.
    const newParties = hydrated.parties.map(p => ({ ...p, source: 'case-roster' as const }));
    const newAttorneys = hydrated.attorneys.map(a => ({ ...a, source: 'case-roster' as const }));
    setParties(newParties);
    setAttorneys(newAttorneys);

    // Remap per-doc metadata references (FILING_PARTY, PARTY_SERVED,
    // FILING_ATTORNEY, …) from the pre-swap local ids to the new roster ids,
    // synchronously with the swap. Without this the references orphan at the old
    // ids and an immediate fee-calc fails (EFM 4082) until the DocumentsTab
    // reconciliation effect runs on a later render / Edit-Draft reload — and
    // that effect can only DROP orphans (it never sees the old roster), forcing
    // the user to re-check Party Served / Filing Attorney. Here we still hold
    // both rosters, so we carry the references over by stable identity.
    const patches = remapMetadataReferences({
      documents: wizardState.documents,
      oldParties,
      newParties,
      oldAttorneys,
      newAttorneys,
    });
    for (const { docId, metadataValues } of patches) {
      // SPEC-SOR-220-v2: this write IS the case change, so it goes in with the
      // `case_change_clear` write kind — every code the remap REPORTED dropped
      // lands empty with provenance `cleared` (the writer's emptiness guard
      // keeps a retargeted survivor's code at its existing provenance), while
      // every code the patch does not name keeps both value and provenance.
      updateDocumentMetadata(docId, prev => ({ ...prev, ...metadataValues }), 'case_change_clear');
    }

    // SPEC-SOR-174 R18: the case, parties and documents sections revert to
    // `pending` and the filer's confirm act on those three is discarded — an
    // approval granted for the previous case is not an approval of this one.
    // `sec_court` is deliberately untouched: the filing court did not change.
    //
    // Confirm-flow gated, like every other guided-section write (R16 keeps the
    // model inert in the classic flow, which offers no confirm control and
    // writes no status).
    if (confirmFlowEnabled) revertCaseAttachSections();
  }, [
    updateCaseInfo,
    setParties,
    setAttorneys,
    updateDocumentMetadata,
    confirmFlowEnabled,
    revertCaseAttachSections,
    wizardState.documents,
    wizardState.parties,
    wizardState.attorneys,
  ]);

  const handleSubcaseChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value;
    const selected = subcases.find(s => s.id === selectedId);
    updateCaseInfo({
      complaintId: selectedId,
      complaintTitle: selected?.title || '',
    });
  }, [subcases, updateCaseInfo]);

  return (
    <>
      <CaseSearch
        brand={brand}
        county={county}
        onCaseSelected={handleCaseSelected}
        anchorId={anchorId}
        initialCaseNumber={caseSeed}
      />
      {subcases.length > 1 && (
        <FormRadioGroup
          brand={brand}
          label="Subcase"
          name="subcase"
          value={wizardState.caseInfo.complaintId}
          onChange={handleSubcaseChange}
          options={subcases.map(s => ({
            value: s.id,
            label: `${s.title} (${s.type})`,
          }))}
        />
      )}
      {subcases.length === 1 && (
        <div className="mb-4">
          <span
            className="block text-sm font-medium mb-1"
            style={{ color: brand.colors.text }}
          >
            Subcase
          </span>
          <p
            className="text-sm"
            style={{ color: brand.colors.textLight }}
          >
            {subcases[0].title} ({subcases[0].type})
          </p>
        </div>
      )}
    </>
  );
};

export default CaseAttachPanel;
